import type { WebViewDto } from '@/api/webs'
import { BRAND_CLASSES } from '@/constants/theme'
import { CheckCircle, LockOpen } from 'lucide-preact'
import { WebSignOffModal } from './modals/WebSignOffModal'
import { WebReopenModal } from './modals/WebReopenModal'
import { useWebSignOff } from '@/hooks/useWebSignOff'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'

interface Props {
    web: WebViewDto
    setWeb: (updater: WebViewDto | ((prev: WebViewDto | null) => WebViewDto | null)) => void
    workspaceId: string
    campaignId: string
    webId: string
    hasBigMarker: boolean
    integrationError?: boolean
    /** Sign-off pushes speakers, so it waits for the one-time BigMarker speaker import (ORB-942). */
    importingSpeakers?: boolean
}

export function WebApprovalControls({ web, setWeb, workspaceId, campaignId, webId, hasBigMarker, integrationError, importingSpeakers }: Props) {
    const {
        showSignOffModal, setShowSignOffModal,
        showReopenModal, setShowReopenModal,
        handleSignOff, handleReopen,
        signingOff, reopening,
    } = useWebSignOff({ workspaceId, campaignId, webId, web, setWeb, hasBigMarker })
    const { canEdit } = useWorkspaceCapabilities()

    const badgeClass = web.ApprovalStatus === 'Signed Off'
        ? BRAND_CLASSES.STATUS.SUCCESS
        : BRAND_CLASSES.STATUS.PENDING

    // ⚑ "Ready for Sign-Off" is the DEFAULT ApprovalStatus (WebModel.cs), not a claim that the web is
    // ready — so every unsigned web wore it, sitting beside a readiness chip that said the opposite.
    // "2 before sign-off · Ready for Sign-Off" is a contradiction on its face (ORB-889).
    //
    // Hidden rather than removed: Signed Off, Feedback Submitted and Reopened are all real states a
    // reader cannot get anywhere else in this header, and Signed Off carries the who and when in its
    // tooltip. The default is the only one that says nothing, and the readiness chip plus the Sign Off
    // button beside it already say it better.
    const showApprovalBadge = web.ApprovalStatus !== 'Ready for Sign-Off'

    // The server rejects a sign-off while anything is outstanding, so offering the button would only
    // buy the user a 422. The readiness chip sits immediately before this and carries the detail, so
    // the disabled state is never unexplained (ORB-460).
    const outstandingCount = web.OutstandingForSignOff?.length ?? 0
    const signOffBlocked = integrationError || importingSpeakers || outstandingCount > 0
    const blockedReason = integrationError
        ? 'Sign-off is unavailable while the BigMarker connection is in an error state'
        : importingSpeakers
            ? 'Sign-off waits until the speakers have been imported from BigMarker'
        : outstandingCount > 0
            ? `${outstandingCount} ${outstandingCount === 1 ? 'item is' : 'items are'} still needed before sign-off`
            : undefined

    return (
        <>
            {showApprovalBadge && (
                <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${badgeClass}`}
                    title={web.ApprovalStatus === 'Signed Off' && web.ApprovedByName
                        ? `Signed off by ${web.ApprovedByName} on ${web.ApprovedAt ? new Date(web.ApprovedAt).toLocaleDateString() : '—'}`
                        : undefined}
                >
                    <span className="w-2 h-2 rounded-full bg-current opacity-70" />
                    {web.ApprovalStatus}
                </span>
            )}
            {web.ApprovalStatus !== 'Signed Off' && canEdit && (
                <button
                    type="button"
                    onClick={() => setShowSignOffModal(true)}
                    disabled={signOffBlocked}
                    title={blockedReason}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        signOffBlocked
                            ? 'bg-slate-300 text-slate-500 cursor-not-allowed dark:bg-slate-600 dark:text-slate-400'
                            : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                >
                    <CheckCircle className="w-3.5 h-3.5" /> Sign Off
                </button>
            )}
            {web.ApprovalStatus === 'Signed Off' && canEdit && (
                <button
                    type="button"
                    onClick={() => setShowReopenModal(true)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                    title="Reopen"
                >
                    <LockOpen className="w-3.5 h-3.5" />
                </button>
            )}
            <WebSignOffModal
                isOpen={showSignOffModal}
                onClose={() => setShowSignOffModal(false)}
                onConfirm={handleSignOff}
                loading={signingOff}
                hasIntegration={hasBigMarker}
                hasBigMarkerEvent={!!web.OnlineEvent?.ConferenceId}
                workspaceId={workspaceId}
                webId={webId}
                mayPublishLandingPage={!!(web.Download?.Quiz || web.Download?.SignedOffQuiz)}
                storedSlug={web.Slug}
                // Whichever sub-entity holds the public title; the slug suggestion derives from it,
                // never from Name, which is an internal planning code (ORB-459).
                publicTitle={web.OnlineEvent?.Title || web.Download?.Title || web.Meeting?.Title || web.Demo?.Title || undefined}
                frozen={!!web.ApprovedAt}
            />
            <WebReopenModal isOpen={showReopenModal} onClose={() => setShowReopenModal(false)} onConfirm={handleReopen} loading={reopening} />
        </>
    )
}
